import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { cn } from '#/lib/utils';
import { Checkbox } from '#/modules/commonComponents/checkbox';
import AppDialog from '#/modules/commonComponents/dialog/AppDialog';
import {
  dialogContentCompactGapClassName,
  dialogWideMaxWidthClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import { SelectCore } from '#/modules/commonComponents/form/select';
import TableSearchInput from '#/modules/commonComponents/table/TableSearchInput';
import MaterialNameStack from '#/modules/imsMgmt/receipts/shared/MaterialNameStack';
import {
  buildTransferPickerWarehouseOptions,
  filterTransferPickerCandidates,
} from '#/modules/momMgmt/workOrder/transfer/helper/filterTransferPickerCandidates';
import { calcUnsatisfiedQty } from '#/modules/momMgmt/workOrder/transfer/helper/transferShortage';
import { clampQtyToAvailable } from '#/utils/quantityBounds';
import { toast } from '#/utils/toast';

function StatCell({ label, value, emphasize = false }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
      <p className="text-xs text-gray-500">{label}</p>
      <p
        className={cn(
          'mt-0.5 text-base font-semibold',
          emphasize ? 'text-amber-700' : 'text-gray-900',
        )}
      >
        {value}
      </p>
    </div>
  );
}

StatCell.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  emphasize: PropTypes.bool,
};

function formatPlaceLabel(row) {
  return [row?.warehouseLabel, row?.locationLabel].filter(Boolean).join(' / ') || '—';
}

/**
 * 需求物料選庫。僅勾選；確認後 qty＝可用量、policy＝全轉。
 * 舊「dialog 內編數量」見 ORD-work-order-transfer-picker-qty-sync.md。
 */
const WorkOrderTransferPickerDialog = ({
  open,
  onOpenChange,
  material,
  initialSelection = {},
  onConfirm,
  excludeWarehouseId = null,
  excludeWarehouseIds = null,
  showOnHandQty = true,
}) => {
  /** null＝本次開啟尚未編輯，直接沿用 draft 帶入值（勿用 effect 同步）。 */
  const [selectionDraft, setSelectionDraft] = useState(null);
  const [candidateKeyword, setCandidateKeyword] = useState('');
  const [warehouseFilterId, setWarehouseFilterId] = useState('');

  const invRows = useMemo(
    () => material?.transferableInventoryStocks ?? [],
    [material],
  );
  const selection = selectionDraft ?? initialSelection;

  const warehouseSelectOptions = useMemo(() => {
    const fromRows = buildTransferPickerWarehouseOptions(invRows, excludeWarehouseId, {
      excludeWarehouseIds,
    });
    return [
      { value: '', label: '全部' },
      ...fromRows,
    ];
  }, [excludeWarehouseId, excludeWarehouseIds, invRows]);

  const operatingQty = useMemo(
    () => invRows
      .filter((row) => selection[row.id] != null)
      .reduce((sum, row) => sum + (Number(row.availableQty) || 0), 0),
    [invRows, selection],
  );

  const unsatisfiedQty = calcUnsatisfiedQty(
    material?.requiredQty,
    material?.onHandQty,
    operatingQty,
    { includeOnHand: showOnHandQty },
  );

  const warehouseFilteredInvRows = useMemo(
    () => filterTransferPickerCandidates(invRows, {
      keyword: '',
      selectedWarehouseIds: warehouseFilterId ? [warehouseFilterId] : [],
      excludeWarehouseId,
      excludeWarehouseIds,
      material,
    }),
    [excludeWarehouseId, excludeWarehouseIds, invRows, material, warehouseFilterId],
  );

  const filteredInvRows = useMemo(
    () => filterTransferPickerCandidates(invRows, {
      keyword: candidateKeyword,
      selectedWarehouseIds: warehouseFilterId ? [warehouseFilterId] : [],
      excludeWarehouseId,
      excludeWarehouseIds,
      material,
    }),
    [
      candidateKeyword,
      excludeWarehouseId,
      excludeWarehouseIds,
      invRows,
      material,
      warehouseFilterId,
    ],
  );

  function handleOpenChange(nextOpen) {
    if (!nextOpen) {
      setSelectionDraft(null);
      setCandidateKeyword('');
      setWarehouseFilterId('');
    }
    onOpenChange(nextOpen);
  }

  function handleToggle(invId, checked) {
    setSelectionDraft((prev) => {
      const next = { ...(prev ?? initialSelection) };
      if (checked) {
        next[invId] = true;
      } else {
        delete next[invId];
      }
      return next;
    });
  }

  function handleSearchSelectByStockNo() {
    const code = candidateKeyword.trim();
    if (!code) {
      return;
    }
    const needle = code.toLowerCase();
    const match = warehouseFilteredInvRows.find(
      (row) => String(row.stockNo ?? '').toLowerCase() === needle,
    );
    if (!match) {
      toast.error(`找不到庫存編號：${code}`);
      return;
    }
    if (selection[match.id] == null) {
      handleToggle(match.id, true);
    }
    setCandidateKeyword('');
  }

  function handleConfirm() {
    const lines = invRows
      .filter((row) => selection[row.id] != null)
      .map((row) => {
        const availableQty = row.availableQty;
        const qty = clampQtyToAvailable(availableQty, availableQty);
        return {
          mtrId: material.mtrId,
          mtrNo: material.mtrNo,
          mtrName: material.mtrName,
          invStockId: row.id,
          stockNo: row.stockNo,
          qty,
          availableQty,
          unitId: '',
          unitOptions: [],
          fromWarehouseLabel: formatPlaceLabel(row),
          stockNoPolicy: 'TRANSFERRED_KEEP_OLD',
        };
      })
      .filter((line) => line.qty > 0);

    onConfirm(lines);
    handleOpenChange(false);
  }

  const title = material
    ? `選庫 — ${material.mtrName || material.mtrNo || material.mtrId}`
    : '選庫';

  const headerSearch = (
    <div className="flex w-full max-w-md items-center gap-2">
      <TableSearchInput
        useKeyword
        keyword={candidateKeyword}
        onKeywordChange={setCandidateKeyword}
        onSearch={handleSearchSelectByStockNo}
        placeholder="搜尋或輸入庫存編號後按 Enter 選取"
        className="min-w-0 w-full text-foreground"
      />
    </div>
  );

  return (
    <AppDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={title}
      description="勾選他倉庫存；確認後以可用量寫入右側明細（可再於明細調整數量與庫編政策）。"
      headerVariant="primary"
      headerEnd={headerSearch}
      showCloseButton
      maxWidthClassName={dialogWideMaxWidthClassName}
      contentClassName={dialogContentCompactGapClassName}
      closeOnOutsideClick={false}
      onCancel={() => handleOpenChange(false)}
      onConfirm={handleConfirm}
      confirmText="確認"
      cancelText="取消"
    >
      <div className="space-y-4">
        <div
          className={cn(
            'grid grid-cols-2 gap-2 sm:grid-cols-2',
            showOnHandQty ? 'lg:grid-cols-5' : 'lg:grid-cols-4',
            'sticky top-0 z-10 border-b border-slate-200 bg-panel pb-4',
          )}
        >
          <StatCell label="當前需求" value={material?.requiredQty ?? '—'} />
          {showOnHandQty ? (
            <StatCell label="當前庫存" value={material?.onHandQty ?? '—'} />
          ) : null}
          <StatCell label="尚未滿足數量" value={unsatisfiedQty} emphasize />
          <StatCell label="操作中數量" value={operatingQty} />
          <SelectCore
            id="work-order-transfer-picker-warehouse"
            label="當前倉別"
            options={warehouseSelectOptions}
            value={warehouseFilterId}
            onChange={(next) => setWarehouseFilterId(next ?? '')}
            hideErrorMessage
            containerClassName="mt-2.5 mb-0!"
            className="w-full"
          />
        </div>

        {invRows.length === 0 ? (
          <p className="text-sm text-muted-foreground">尚無可轉入庫存。</p>
        ) : filteredInvRows.length === 0 ? (
          <p className="text-sm text-muted-foreground">無符合篩選條件的庫存。</p>
        ) : (
          <ul className="m-0 list-none divide-y divide-slate-200 rounded-md border border-slate-200 p-0">
            {filteredInvRows.map((row, index) => {
              const checked = selection[row.id] != null;
              const availableQty = Number(row.availableQty) || 0;
              return (
                <li
                  key={`${row.id || 'stock'}-${index}`}
                  role="option"
                  aria-selected={checked}
                  className={cn(
                    'flex cursor-pointer items-center justify-between gap-4 p-3 transition-colors',
                    'sm:grid-cols-[auto_minmax(0,1.2fr)_minmax(0,1fr)_auto]',
                    'sm:items-center',
                    checked
                      ? 'bg-primary-100 shadow-inner hover:bg-primary-100'
                      : 'hover:bg-slate-50',
                  )}
                  onClick={() => handleToggle(row.id, !checked)}
                >
                  <div className="flex items-center gap-4">
                    <div
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => event.stopPropagation()}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(value) => handleToggle(row.id, value === true)}
                        aria-label={`選取 ${row.stockNo}`}
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="font-mono text-sm text-gray-800">{row.stockNo || '—'}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {formatPlaceLabel(row)}
                      </p>
                    </div>
                    <MaterialNameStack
                      name={material?.mtrName}
                      code={material?.mtrNo}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground sm:text-right">
                    可用量：
                    <span className="text-base font-medium text-slate-800 tabular-nums">
                      {availableQty || '—'}
                    </span>
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </AppDialog>
  );
};

WorkOrderTransferPickerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  material: PropTypes.shape({
    mtrId: PropTypes.string,
    mtrNo: PropTypes.string,
    mtrName: PropTypes.string,
    requiredQty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onHandQty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    transferableInventoryStocks: PropTypes.array,
  }),
  initialSelection: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.bool, PropTypes.string]),
  ),
  onConfirm: PropTypes.func.isRequired,
  excludeWarehouseId: PropTypes.string,
  excludeWarehouseIds: PropTypes.arrayOf(PropTypes.string),
  showOnHandQty: PropTypes.bool,
};

export default WorkOrderTransferPickerDialog;
